/**
 * @copyright Microsoft Corporation. All rights reserved.
 *
 * @file File Hover Card component
 */

import * as React from 'react';
import {
  HoverCard,
  IExpandingCardProps
} from 'office-ui-fabric-react/lib/HoverCard';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { DirectionalHint } from 'office-ui-fabric-react/lib/common/DirectionalHint';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { getRelativeDateTimeStringPast } from '@ms/utilities-date-time/lib/relativePast';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { withCurrentItem } from '../decorators/selectors';
import {
  dateModifiedFacet,
  displayNameFacet,
  fileNameFacet,
  analyticsItemFacet,
  modifiedByPersonFacet
} from '../../facets/ItemFacets';
import { emailFacet, sipFacet, pictureFacet } from '../../facets/PersonFacets';
import {
  activitiesFacet,
  lifeCycleStatusFacet,
  incompleteDataFacet,
  isTrendingFacet,
  keyPointsFacet,
  viewCountFacet,
  viewerCountFacet
} from '../../facets/ItemAnalyticsFacets';
import { IActivity, IUser, IKeyPointsData, ILifeCycleStatus } from '../../models/ItemAnalytics';
import { getTheme, ITheme, mergeStyles, IStyle } from 'office-ui-fabric-react/lib/Styling';
import { AnalyticsActivity } from './hoverCardActivity/AnalyticsActivity';
import { AnalyticsActivityList } from './hoverCardActivityList/AnalyticsActivityList';
import { AnalyticsActionBar } from './hoverCardActionBar/AnalyticsActionBar';
import { AnalyticsLifeCycle } from './hoverCardLifeCycle/AnalyticsLifeCycle';
import { AnalyticsKeyPoints } from './hoverCardKeyPoints/AnalyticsKeyPoints';
import { isSupported } from './FileHoverCardHelper';
import { logEngagement } from '../../operations/logEngagement';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import strings from './FileHoverCard.resx';
import * as FileHoverCardStyle from './FileHoverCard.scss';
import { isCurrentLoginUser } from './UserNameHelper';
import { localeFacet, configurationItemKey } from '../../facets/ConfigurationFacets';

const EXPANDED_CARD_HEIGHT = 340;
const EXPANDED_CARD_KEYPOINTS_HEIGHT = 340; // Set as default height first, need design discussion result for this.
const EXPANDED_CARD_LIFECYCLE_ONLY_HEIGHT = 70;

const actions = [{
  key: 'share',
  icon: 'share',
  title: 'share',
  onClick: () => { alert('share clicked'); }
}];

function cleanCachedItem(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('cleanCachedItem called');
}

function onAddMetadataNow(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('onAddMetadataNow Click');
}

function onCheckIn(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('onCheckIn coming soon');
}

function onDiscardCheckOut(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('onDiscardCheckOut coming soon');
}

function onKeyPointsClick(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('onKeyPointsClick coming soon');
}

function onView(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('onView, coming soon');
}

function onViewPolicyTip(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
  event && alert('onViewPolicyTip coming soon');
}

export interface IFileHoverCardProps {
  // props from parent:
  cardDismissDelay?: number;
  directionalHint?: DirectionalHint;
  gapSpace?: number;

  // props from facets:
  actionBarItems?: IContextualMenuItem[];
  itemKey: string | undefined;
  loginUserEmail?: string;
  userEmail?: string;
  target?: HTMLElement;
  // actions that may be abled to implemented in items-view:
  cleanCachedItem?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onAddMetadataNow?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onCheckIn?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onDetails?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onDiscardCheckOut?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onKeyPointsClick?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onView?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onViewPolicyTip?: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
}

export interface IFileHoverCardState {
  visible: boolean;
}

// TODO: add responsive mode: @withResponsiveMode
export class FileHoverCard extends React.Component<IFileHoverCardProps, IFileHoverCardState> {
  public static defaultProps: IFileHoverCardProps = {
    itemKey: '',
    actionBarItems: actions,
    cleanCachedItem: cleanCachedItem,
    onAddMetadataNow: onAddMetadataNow,
    onCheckIn: onCheckIn,
    onDiscardCheckOut: onDiscardCheckOut,
    onKeyPointsClick: onKeyPointsClick,
    onView: onView,
    onViewPolicyTip: onViewPolicyTip
  };

  constructor(props: IFileHoverCardProps) {
    super(props);
    this.state = { visible: false };
  }

  public render(): JSX.Element {
    const {
      itemKey,
      cardDismissDelay = 300,
      directionalHint = DirectionalHint.rightTopEdge,
      gapSpace = 8
    } = this.props;

    return (
      <WithItemCache>{ ({ itemCache }: IItemCacheStateProps): JSX.Element => {
        let expandCardHeight = 0;

        if (this.state.visible) { // Only send demand request when card is opening
          const analyticsKey = itemCache.demandItemFacet(analyticsItemFacet, itemKey);
          const itemAnalyticsKey = analyticsKey && analyticsKey.itemKey;
          const isTrending = itemCache.demandItemFacet(isTrendingFacet, itemAnalyticsKey);
          const activities = itemCache.demandItemFacet(activitiesFacet, itemAnalyticsKey);

          expandCardHeight = this._getCardHeight(isTrending, activities);
        }

        const expandingCardProps: IExpandingCardProps = {
          onRenderCompactCard: this._onRenderCompactContent,
          onRenderExpandedCard: this._onRenderExpandedContent,
          compactCardHeight: 176,
          expandedCardHeight: expandCardHeight,
          directionalHint: directionalHint,
          directionalHintFixed: false,
          gapSpace: gapSpace,
          styles: {
            expandedCardScroll: 'ms-FileHoverCard-expandedCard-hostScroll',
          }
        };

        return (
          <HoverCard
            expandingCardProps={ expandingCardProps }
            expandedCardOpenDelay={ 1500 }
            cardDismissDelay={ cardDismissDelay }
            target={ this.props.target }
            onCardVisible={ this._onCardOpen }
            onCardHide={ this._onCardHide }
            trapFocus={ true }
          />
        );
      } }</WithItemCache>
    );
  }

  @autobind
  private _getCardHeight(
    isTrending?: boolean,
    activities?: IActivity[],
    keyPointsData?: IKeyPointsData,
    lifeCycleStatus?: ILifeCycleStatus[]
  ): number {
    if (this._shouldShowActivities(activities)) {
      // If activities are available, show full expand card
      return EXPANDED_CARD_HEIGHT;
    } else if (this._isKeyPointsAvailable(keyPointsData)) {
      // If keypoints section (with lifecycle or without lifecycle) is available
      return EXPANDED_CARD_KEYPOINTS_HEIGHT;
    } else if (isTrending) {
      return EXPANDED_CARD_LIFECYCLE_ONLY_HEIGHT;
    } else if (lifeCycleStatus && this._isLifecycleAvailable(lifeCycleStatus)) {
      return EXPANDED_CARD_LIFECYCLE_ONLY_HEIGHT * lifeCycleStatus.length;
    }

    return 1;
  }

  @autobind
  private _shouldShowActivities(activities?: IActivity[]): boolean {
    const { loginUserEmail, userEmail } = this.props;
    return (!!activities &&
      (activities.length > 1 ||
        (activities.length === 1 && !isCurrentLoginUser(activities[0].actor.user.email, loginUserEmail, userEmail))));
  }

  @autobind
  private _isKeyPointsAvailable(keyPoints?: IKeyPointsData): boolean {
    const readTime = keyPoints && keyPoints.readTime && keyPoints.readTime.minutes;
    return !!(keyPoints && keyPoints.keyPoints && keyPoints.keyPoints.length > 0) || !!readTime;
  }

  @autobind
  private _isLifecycleAvailable(lifeCycleStatus?: ILifeCycleStatus[]): boolean {
    // status = 0 is available status so need check if it is undefined
    return !!(lifeCycleStatus && (lifeCycleStatus !== undefined));
  }

  @autobind
  private _onCardOpen(): void {
    logEngagement({ name: 'FileHoverCard.CompactCardOpen' });
    this.setState({ visible: true });
  }

  private _onCardHide = (): void => {
    this.setState({ visible: false });
  }

  @autobind
  private _onRenderCompactContent(): JSX.Element {
    const { itemKey, actionBarItems } = this.props;

    return (
      <WithItemCache>{ ({ itemCache }: IItemCacheStateProps): JSX.Element => {
        const itemName = itemCache.demandItemFacet(fileNameFacet, itemKey);
        const itemTitle = itemName && itemName.name;
        const extension = itemName && itemName.name;

        const dateModifiedMilliSeconds = itemCache.demandItemFacet(dateModifiedFacet, itemKey);
        const modifiedByPersonKey = itemCache.demandItemFacet(modifiedByPersonFacet, itemKey);
        const analyticsKey = itemCache.demandItemFacet(analyticsItemFacet, itemKey);
        const itemAnalyticsKey = analyticsKey && analyticsKey.itemKey;

        const dateModified = dateModifiedMilliSeconds ? new Date(dateModifiedMilliSeconds) : new Date();
        let modifiedByUser;
        if (modifiedByPersonKey) {
          modifiedByUser = itemCache.demandItem({
            displayName: displayNameFacet,
            email: emailFacet,
            sip: sipFacet,
            picture: pictureFacet
          }, modifiedByPersonKey.itemKey);
        }

        const modifiedUser: IUser = {
          email: modifiedByUser && modifiedByUser.email || '',
          id: modifiedByUser && modifiedByUser.sip || '',
          displayName: modifiedByUser && modifiedByUser.displayName || '',
          image: modifiedByUser && modifiedByUser.picture || ''
        };

        const locale = itemCache.demandItemFacet(localeFacet, configurationItemKey);

        const language = locale && locale.language || window.navigator.language;

        const currentUserActivity: IActivity = {
          activityDateTime: getRelativeDateTimeStringPast(dateModified, language),
          actor: {
            user: modifiedUser
          }
        };

        const viewCount = itemCache.demandItemFacet(viewCountFacet, itemAnalyticsKey);
        const viewerCount = itemCache.demandItemFacet(viewerCountFacet, itemAnalyticsKey);
        const activities = itemCache.demandItemFacet(activitiesFacet, itemAnalyticsKey);

        const seeDetailsCommand = {
          key: 'details',
          title: strings.seeDetails,
          name: strings.seeDetails,
          ariaLabel: strings.seeDetails,
          onClick: this._onDetails
        };
        const commands = this.props.onDetails ? [seeDetailsCommand] : [];

        return (
          <div
            className={ `${FileHoverCardStyle.msFileHoverCardCompactCard}` }
            aria-labelledby={ `${FileHoverCardStyle.msFileHoverCardCompactCardTitle} ms-FileHoverCard-compactCard--latestActivity` }
          >
            <div
              className={ `${FileHoverCardStyle.msFileHoverCardCompactCardTitle}` }
              id='ms-FileHoverCard-title'
              aria-label={ StringHelper.format(strings.hoverCardTitle, itemTitle) }
            >
              { itemTitle }
            </div>
            <div className={ `${FileHoverCardStyle.msFileHoverCardCompactCardActivity}` } id='ms-FileHoverCard-compactCard--latestActivity'>
              <AnalyticsActivity
                activity={ currentUserActivity }
                loginUserEmail={ this.props.loginUserEmail }
                userEmail={ this.props.userEmail }
              />
            </div>
            <div className='ms-FileHoverCard-compactCard--actionBar'>
              {
                actionBarItems &&
                <AnalyticsActionBar
                  analyticsStats={
                    {
                      viewCount: viewCount,
                      viewerCount: viewerCount,
                      activities: activities,
                      onClick: this._onView,
                      isNotSupported: !(extension && isSupported(extension))
                    }
                  }
                  items={ actionBarItems }
                  withFacePile={ true }
                  farItems={ commands }
                />
              }
            </div>
          </div>
        );
      } }</WithItemCache>
    );
  }

  @autobind
  private _onRenderExpandedContent(): JSX.Element | null {
    const { itemKey } = this.props;
    const theme: ITheme = getTheme();
    return (
      <WithItemCache>{ ({ itemCache }: IItemCacheStateProps): JSX.Element => {
        const analyticsKey = itemCache.demandItemFacet(analyticsItemFacet, itemKey);
        const itemAnalyticsKey = analyticsKey && analyticsKey.itemKey;

        const viewCount = itemCache.demandItemFacet(viewCountFacet, itemAnalyticsKey);
        const viewerCount = itemCache.demandItemFacet(viewerCountFacet, itemAnalyticsKey);
        const isTrending = itemCache.demandItemFacet(isTrendingFacet, itemAnalyticsKey);
        const activities = itemCache.demandItemFacet(activitiesFacet, itemAnalyticsKey);
        const inCompleteData = itemCache.demandItemFacet(incompleteDataFacet, itemAnalyticsKey);
        const keyPointsData = itemCache.demandItemFacet(keyPointsFacet, itemAnalyticsKey);
        const lifeCycleStatus = itemCache.demandItemFacet(lifeCycleStatusFacet, itemAnalyticsKey);

        // const showKeyPoints = isKeyPointsEnabled() && this._isKeyPointsAvailable(keyPointsData);
        const showKeyPoints = this._isKeyPointsAvailable(keyPointsData);
        const showActivities = this._shouldShowActivities(activities);
        // const fileHoverCardClassName = 'ms-FileHoverCard-expandedCard' + (isFluentActive ? ' isFluent' : '');

        const VISIBLE_LIFECYCLE_COUNT = 2;
        const lifeCycleElement = [];

        if (lifeCycleStatus && lifeCycleStatus.length > 0) {
          for (let count = 0; count < Math.min(VISIBLE_LIFECYCLE_COUNT, lifeCycleStatus.length); count++) {
            lifeCycleStatus[count] && lifeCycleElement.push(this._getAnalyticsLifeCycle(lifeCycleStatus[count]));
          }
        }

        if (!viewCount || !viewerCount || !activities) {
          return <div />;
        }

        return (
          <div className={ `${FileHoverCardStyle.msFileHoverCardExpandedCard}` }>
            {
              // Show 2 lifecycles on Hover Card
              lifeCycleStatus
                ? lifeCycleElement
                : isTrending && this._getMessageSection('Market', strings.trendingItem, { color: theme.palette.green })
            }
            {
              (showKeyPoints || showActivities) &&
              <div className={ `${FileHoverCardStyle.msFileHoverCardExpandedCardScrollableSection}` }>
                {
                  showKeyPoints &&
                  <AnalyticsKeyPoints
                    keyPointsData={ keyPointsData }
                    onKeyPointsClick={ this.props.onKeyPointsClick }
                  />
                }
                {
                  showActivities && <div className={ `${FileHoverCardStyle.msFileHoverCardActivitiesSection}` }>
                    <AnalyticsActivityList
                      viewCount={ viewCount }
                      viewerCount={ viewerCount }
                      isTrending={ isTrending }
                      activities={ activities }
                      inCompleteData={ inCompleteData }
                      // TODO: add page loading later:
                      // onLoadMoreActivities={ () => this._store.fetchItemAnalytics(this.props.item, skipToken) }
                      loginUserEmail={ this.props.loginUserEmail }
                      userEmail={ this.props.userEmail }
                    />
                  </div>
                }
              </div>
            }
          </div>
        );
      } }</WithItemCache>
    );
  }

  private _getAnalyticsLifeCycle(lifeCycleStatus: ILifeCycleStatus): JSX.Element {
    return (
      <AnalyticsLifeCycle
        lifeCycleStatus={ lifeCycleStatus }
        loginUserEmail={ this.props.loginUserEmail }
        userEmail={ this.props.userEmail }
        onViewPolicyTip={ this.props.onViewPolicyTip }
        onAddMetadataNow={ this.props.onAddMetadataNow }
        onCheckIn={ this.props.onCheckIn }
        onDiscardCheckOut={ this.props.onDiscardCheckOut }
        cleanCachedItem={ this._cleanCachedItem }
      />
    );
  }

  @autobind
  private _getMessageSection(iconName: string, content: string, iconStyle?: IStyle): JSX.Element {
    const baseIconStyle: IStyle = {
      paddingRight: '8px',
      position: 'relative',
      top: '3px'
    };

    return (
      <div className={ `${FileHoverCardStyle.msFileHoverCardExpandedCardSection}` }>
        <Icon
          iconName={ iconName }
          styles={ {
            root: mergeStyles([
              baseIconStyle,
              iconStyle
            ])
          } }
        />
        { content }
      </div>
    );
  }

  @autobind
  private _onView(ev: React.MouseEvent<HTMLElement>): void {
    logEngagement({ name: 'FileHoverCard.ViewCountClick' });
    this.props.onView && this.props.onView(ev);

    ev && ev.stopPropagation();
    ev && ev.preventDefault();
  }

  @autobind
  private _onDetails(ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
    logEngagement({ name: 'FileHoverCard.SeeDetailsClick' });
    this.props.onDetails && this.props.onDetails(ev);

    ev && ev.stopPropagation();
    ev && ev.preventDefault();
  }

  @autobind
  private _cleanCachedItem(): void {
    console.log('Cache clean requested');
  }
}

export const CurrentFileHoverCard = withCurrentItem()(FileHoverCard);
